import React, { useState } from "react";
import { toast } from "react-toastify";
import { Carousel } from "@ark-ui/react";
import { useSwipeable } from "react-swipeable";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { makeRequest } from "../../../lib/request";
import Button from "../../atoms/Button";
import { useUserStore } from "../../../store/userStore";

function GalleryImages({ postId, images }: { postId: number; images: any[] }) {
  const user = useUserStore((state) => state.user);
  const roles = useUserStore((state) => state.roles);
  const accessToken = useUserStore((state) => state.accessToken);
  const impersonationToken = useUserStore((state) => state.impersonationToken);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      if (eventData.deltaX > 0) {
        changeDirection("left");
      } else {
        changeDirection("right");
      }
    },
  });

  const deleteImage = async (imageId: number) => {
    if (!user || !roles || !roles.includes("admin")) {
      return;
    }

    const c = window.confirm("Are you sure you want to delete this image?");

    if (!c) {
      return;
    }

    const { status } = await makeRequest(`admin/gallery/image`, {
      method: "DELETE",
      body: JSON.stringify({ postId, imageId }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${impersonationToken || accessToken}`,
      },
    });

    if (!status) {
      toast.error("Failed to delete image.");
    } else {
      toast.success("Successfully deleted image.");
      window.location.reload();
    }
  };

  const changeDirection = (direction: "left" | "right") => {
    if (direction === "left") {
      if (currentIndex === 0) {
        setCurrentIndex(images.length - 1);
      } else {
        setCurrentIndex(currentIndex - 1);
      }
    } else {
      if (currentIndex === images.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  return (
    <div className="relative w-full h-fit">
      <div className="max-md:hidden">
        <div className="grid grid-cols-8 gap-5 h-fit items-stretch">
          <div
            className={`col-span-2 overflow-y-auto max-h-[700px] flex pb-5 ${
              images.length === 1 ? "hidden" : ""
            }`}
          >
            <div className="flex flex-col gap-5 shrink">
              {images.map((image, index) => (
                <Button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  aria-selected={index === currentIndex}
                  aria-label={`Expand image ${currentIndex + 1}`}
                  className={`bg-transparent !rounded-xl !p-0 overflow-hidden shrink-0 focus:outline-none focus-visible:border-blue-600 ${
                    index === currentIndex
                      ? "border-2 border-border-primary"
                      : "focus-visible:border-2"
                  }`}
                >
                  <img
                    src={`https://images.givin.gifts/preset:sharp/resize:fit:540:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${
                      image.image !== "" ? image.image : "gallery/fallback.png"
                    }`}
                    alt=""
                    className="object-cover hover:scale-105 transition-transform duration-300 ease-in-out transform"
                    loading="lazy"
                    width={480}
                    height={480}
                  />
                </Button>
              ))}
            </div>
          </div>
          <div
            className={`${images.length === 1 ? "col-span-8" : "col-span-6"}`}
          >
            <div className="relative">
              <a
                href={`https://images.givin.gifts/preset:sharp/resize:fit:720:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${
                  images[currentIndex].image !== ""
                    ? images[currentIndex].image
                    : "gallery/fallback.png"
                }`}
                target="_blank"
                className="group focus:outline-none"
                aria-label={`View image ${currentIndex + 1} of ${
                  images.length
                }`}
              >
                <img
                  src={`https://images.givin.gifts/preset:sharp/resize:fit:1280:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${
                    images[currentIndex].image !== ""
                      ? images[currentIndex].image
                      : "gallery/fallback.png"
                  }`}
                  className="object-cover h-full w-full rounded-xl group-focus:border-2 group-focus:border-blue-600"
                  alt=""
                  loading="eager"
                  width={1280}
                  height={1280}
                />
              </a>
              {roles && roles.includes("admin") ? (
                <div className="absolute bottom-2 right-2">
                  <Button
                    style="teritary"
                    className={`justify-center flex flex-row gap-2 items-center ml-auto mr-0 rounded-lg`}
                    onClick={() => {
                      deleteImage(images[currentIndex].id);
                    }}
                  >
                    <TrashIcon className="w-6 h-6 text-rag-error" />
                    <span className="sr-only text-text-base font-semibold">
                      Delete image {currentIndex + 1}
                    </span>
                  </Button>
                </div>
              ) : (
                ""
              )}
              <div className="absolute top-2 right-2 bg-surface-base text-text-base text-center font-semibold rounded-lg px-3 py-1">
                {currentIndex + 1} of {images.length}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="md:hidden">
        <Carousel.Root
          align="center"
          loop={true}
          index={currentIndex}
          onIndexChange={(details) => setCurrentIndex(details.index)}
        >
          <Carousel.Viewport className="relative" {...handlers}>
            {images.length === 1 ? (
              ""
            ) : (
              <Carousel.Control className="flex gap-2 w-full absolute top-2 left-2 z-10">
                <Carousel.PrevTrigger className="bg-surface-base rounded-xl shadow-sm hover:bg-surface-lowered hover:shadow-md group p-2">
                  <ArrowLeftIcon className="text-text-base group-hover:text-text-base h-5 w-5" />
                  <span className="sr-only">Previous</span>
                </Carousel.PrevTrigger>
                <Carousel.NextTrigger className="bg-surface-base rounded-xl shadow-sm hover:bg-surface-lowered hover:shadow-md group p-2">
                  <span className="sr-only">Next</span>{" "}
                  <ArrowRightIcon className="text-text-base group-hover:text-text-base h-5 w-5" />
                </Carousel.NextTrigger>
              </Carousel.Control>
            )}
            <Carousel.ItemGroup className="flex flex-row !gap-2">
              {images.map((image, index) => (
                <Carousel.Item key={index} index={index}>
                  <div className="my-auto h-full">
                    <a
                      href={`https://storage.ethereal.sh/gifts-public/${image.image}`}
                      target="_blank"
                      tabIndex={-1}
                      aria-label={`View image ${index + 1} of ${images.length}`}
                    >
                      <img
                        src={`https://images.givin.gifts/preset:sharp/resize:fit:1280:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${image.image}`}
                        className="object-cover h-full w-full rounded-xl"
                        alt=""
                        loading={index === 0 ? "eager" : "lazy"}
                        width={720}
                        height={720}
                      />
                    </a>
                    {roles && roles.includes("admin") ? (
                      <div className="absolute bottom-2 right-2">
                        <Button
                          style="teritary"
                          className={`justify-center flex flex-row gap-2 items-center ml-auto mr-0 rounded-lg`}
                          tabIndex={-1}
                          onClick={() => {
                            deleteImage(image.id);
                          }}
                        >
                          <TrashIcon className="w-6 h-6 text-rag-error" />
                          <span className="sr-only text-text-base font-semibold">
                            Delete image {index + 1}
                          </span>
                        </Button>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </Carousel.Item>
              ))}
            </Carousel.ItemGroup>
            <Carousel.IndicatorGroup className="absolute top-1 right-1 px-2 py-1 flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-1">
                {images.length === 1
                  ? ""
                  : images.map((_, index) => (
                      <Carousel.Indicator
                        key={index}
                        index={index}
                        className={`rounded-full w-[0.75rem] h-[0.75rem] p-0 border focus:ring focus:outline-none ${
                          index === currentIndex
                            ? "bg-black border-white"
                            : "bg-surface-base bg-opacity-30 border-black"
                        }`}
                      >
                        &nbsp;
                      </Carousel.Indicator>
                    ))}
              </div>
              <div className="bg-surface-raised text-text-base text-center font-bold rounded-lg shadow-sm px-3 py-1">
                {currentIndex + 1} of {images.length}
              </div>
            </Carousel.IndicatorGroup>
          </Carousel.Viewport>
        </Carousel.Root>
      </div>
    </div>
  );
}

export default GalleryImages;
